import React, { useState, useMemo, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import {
  Box,
  Grid,
  Container,
  Typography,
  Stack,
  Card,
  IconButton,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Autocomplete,
  TextField,
  Tooltip,
  LinearProgress
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import BarChartIcon from '@mui/icons-material/BarChart';
import GridViewIcon from '@mui/icons-material/GridView';
import Chart from 'react-apexcharts';

import Page from '../components/Page';
import useGlobalSelector from '../store/Slice/Global/GlobalSelector';
import useLoginSelector from '../store/Slice/Login/LoginSelector';
import useDashboardSelector from '../store/Slice/Dashboard/DashboardSelector';
import { getDashboardData, getHierarchyData } from '../store/Slice/Dashboard/DashboardSlice';
import { getBankDropdownData } from '../store/Slice/Global/GlobalSlice';

// ─────────────────────────────────────────────
// FORMATTERS
// ─────────────────────────────────────────────

function formatCurrency(val) {
  if (val === undefined || val === null) return '₹0';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(val);
}

function formatCompactNumber(val) {
  if (val === undefined || val === null || val === 0) return '0';

  if (val >= 10000000) {
    const crValue = val / 10000000;
    return crValue >= 10 ? `${Math.round(crValue)} Cr` : `${crValue.toFixed(1)} Cr`;
  }

  if (val >= 100000) {
    const lValue = val / 100000;
    return lValue >= 10 ? `${Math.round(lValue)} L` : `${lValue.toFixed(1)} L`;
  }

  if (val >= 1000) {
    const kValue = val / 1000;
    return kValue >= 10 ? `${Math.round(kValue)} k` : `${kValue.toFixed(1)} k`;
  }

  return `${val}`;
}

// ─────────────────────────────────────────────
// KPI CARD COMPONENT
// ─────────────────────────────────────────────

function ModernKpiCard({ title, icon, value, subValue, cardBg, borderCol, iconBg, iconCol }) {
  return (
    <Card
      sx={{
        bgcolor: cardBg,
        border: `1px solid ${borderCol}`,
        borderRadius: '16px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.02)',
        p: 2.25,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 6px 18px rgba(0,0,0,0.06)'
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: '10px',
            bgcolor: iconBg,
            color: iconCol,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {icon}
        </Box>
      </Box>

      {/* Main Big Metric */}
      <Box sx={{ mb: 1 }}>
        <Typography
          sx={{
            fontSize: '28px',
            fontWeight: 800,
            color: '#0f172a',
            lineHeight: 1.1,
            mb: 0.5
          }}
        >
          {value}
        </Typography>
        <Typography sx={{ fontSize: '13px', color: '#334155', fontWeight: 600 }}>
          {subValue}
        </Typography>
        <Typography
          sx={{
            fontSize: '11px',
            fontWeight: 700,
            color: '#64748b',
            textTransform: 'uppercase',
            letterSpacing: '0.4px',
            mt: 0.25
          }}
        >
          {title}
        </Typography>
      </Box>
    </Card>
  );
}

ModernKpiCard.propTypes = {
  title: PropTypes.string,
  icon: PropTypes.node,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  subValue: PropTypes.string,
  cardBg: PropTypes.string,
  borderCol: PropTypes.string,
  iconBg: PropTypes.string,
  iconCol: PropTypes.string
};

// ─────────────────────────────────────────────
// CHARTS
// ─────────────────────────────────────────────

function BankBarChart({ data }) {
  const chartData = data;

  const categories = chartData.map((d) => d.bankName);
  const values = chartData.map((d) => (d.totalAmount ? d.totalAmount / 10000000 : 0)); // In Cr

  // Calculate dynamic height so every record has comfortable spacing and doesn't get squeezed
  const calculatedHeight = Math.max(380, chartData.length * 44 + 60);

  // Maximum value for x-axis scale so labels don't get cut off
  const maxVal = Math.max(...values, 1);
  const xMax = Math.ceil(maxVal * 1.25) || 5;

  const options = {
    chart: {
      type: 'bar',
      toolbar: { show: false },
      fontFamily: 'inherit'
    },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: '60%',
        borderRadius: 4,
        dataLabels: {
          position: 'top'
        },
        distributed: true
      }
    },
    colors: [
      '#0047ba',
      '#1a6be6',
      '#2563eb',
      '#3b82f6',
      '#60a5fa',
      '#38bdf8',
      '#0284c7',
      '#0369a1',
      '#64748b',
      '#94a3b8'
    ],
    dataLabels: {
      enabled: true,
      textAnchor: 'start',
      style: {
        colors: ['#0f172a'],
        fontSize: '11.5px',
        fontWeight: 700
      },
      formatter: (val) => formatCompactNumber(Math.round(val * 10000000)),
      offsetX: 14
    },
    xaxis: {
      categories,
      min: 0,
      max: xMax,
      tickAmount: 5,
      labels: {
        style: { colors: '#64748b', fontSize: '11.5px', fontWeight: 500 },
        formatter: (val) => formatCompactNumber(Math.round(val * 10000000))
      },
      title: {
        text: 'Total Amount (₹)',
        style: { color: '#64748b', fontSize: '12px', fontWeight: 600 },
        offsetY: 4
      },
      axisBorder: { show: false },
      axisTicks: { show: false }
    },
    yaxis: {
      labels: {
        style: { colors: '#1e293b', fontSize: '12px', fontWeight: 600 },
        maxWidth: 170
      }
    },
    legend: { show: false },
    grid: {
      borderColor: '#f1f5f9',
      xaxis: { lines: { show: true } },
      yaxis: { lines: { show: false } },
      padding: { right: 55, left: 10 }
    },
    tooltip: {
      theme: 'light',
      custom: ({ dataPointIndex }) => {
        const item = chartData[dataPointIndex];
        if (!item) return '';
        return `
          <div style="padding: 10px 14px; background: #ffffff; border: 1px solid #e2e8f0; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.08); font-size: 12px;">
            <div style="font-weight: 700; color: #0f172a; margin-bottom: 4px;">${
              item.bankName
            }</div>
            <div style="color: #64748b;">Total Volume: <strong style="color: #0284c7;">${formatCurrency(
              item.totalAmount
            )}</strong></div>
            <div style="color: #64748b;">Total Files: <strong>${item.count ?? 0}</strong></div>
          </div>
        `;
      }
    }
  };

  const series = [{ name: 'Total Amount', data: values }];
  return (
    <Box
      sx={{
        width: '100%',
        maxHeight: 460,
        overflowY: 'auto',
        overflowX: 'hidden',
        pr: 0.5,
        '&::-webkit-scrollbar': { width: '6px' },
        '&::-webkit-scrollbar-track': { bgcolor: '#f8fafc', borderRadius: '4px' },
        '&::-webkit-scrollbar-thumb': { bgcolor: '#cbd5e1', borderRadius: '4px' },
        '&::-webkit-scrollbar-thumb:hover': { bgcolor: '#94a3b8' }
      }}
    >
      <Box sx={{ minHeight: calculatedHeight }}>
        <Chart options={options} series={series} type="bar" height={calculatedHeight} />
      </Box>
    </Box>
  );
}

BankBarChart.propTypes = {
  data: PropTypes.array
};

function ProductDonutChart({ data }) {
  const chartData = data;

  const labels = chartData.map((d) => d.productName);
  const series = chartData.map((d) => d.count);

  const colors = [
    '#003399',
    '#00c5d4',
    '#818cf8',
    '#16a34a',
    '#d97706',
    '#0284c7',
    '#14b8a6',
    '#a855f7',
    '#15803d',
    '#eab308',
    '#0052cc'
  ];

  const options = {
    chart: {
      type: 'donut',
      fontFamily: 'inherit'
    },
    labels,
    colors,
    legend: {
      position: 'bottom',
      horizontalAlign: 'center',
      fontSize: '11px',
      labels: { colors: '#475569' },
      markers: { width: 9, height: 9, radius: 5 },
      itemMargin: { horizontal: 7, vertical: 3 }
    },
    dataLabels: {
      enabled: true,
      formatter: (val) => `${val.toFixed(1)}%`,
      style: { fontSize: '10.5px', fontWeight: 700 },
      dropShadow: { enabled: false }
    },
    plotOptions: {
      pie: {
        donut: {
          size: '68%',
          labels: {
            show: true,
            name: {
              show: true,
              fontSize: '12.5px',
              fontFamily: 'inherit',
              fontWeight: 500,
              color: '#64748b',
              offsetY: -8
            },
            value: {
              show: true,
              fontSize: '24px',
              fontFamily: 'inherit',
              fontWeight: 800,
              color: '#0f172a',
              offsetY: 4
            },
            total: {
              show: true,
              showAlways: true,
              label: 'Total',
              fontSize: '12.5px',
              fontFamily: 'inherit',
              fontWeight: 500,
              color: '#64748b',
              formatter: (w) => {
                const total = w.globals.seriesTotals.reduce((a, b) => a + b, 0);
                return total;
              }
            }
          }
        }
      }
    },
    tooltip: {
      y: { formatter: (val) => `${val} cases` }
    }
  };

  return <Chart options={options} series={series} type="donut" height={360} />;
}

ProductDonutChart.propTypes = {
  data: PropTypes.array
};

// ─────────────────────────────────────────────
// DATA TABLES
// ─────────────────────────────────────────────

function DetailedBreakdownTable({ data }) {
  const tableData = data;

  return (
    <TableContainer component={Paper} elevation={0} sx={{ border: 'none' }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: '#f8fafc' }}>
            <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '11.5px', py: 1.5 }}>
              BANK
            </TableCell>
            <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '11.5px' }}>
              PRODUCT
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 700, color: '#475569', fontSize: '11.5px' }}>
              FILES
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 700, color: '#475569', fontSize: '11.5px' }}>
              SANCTIONED
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 700, color: '#475569', fontSize: '11.5px' }}>
              DISBURSED
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 700, color: '#475569', fontSize: '11.5px' }}>
              AMOUNT
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 700, color: '#475569', fontSize: '11.5px' }}>
              CONV %
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {tableData.map((row, index) => {
            const conv = parseFloat(row.conversionPercentage || 0);
            return (
              <TableRow
                key={index}
                sx={{
                  '&:hover': { bgcolor: '#f8fafc' },
                  borderBottom: '1px solid #f1f5f9'
                }}
              >
                <TableCell sx={{ fontWeight: 600, color: '#0f172a', fontSize: '13px', py: 1.5 }}>
                  {row.bankName}
                </TableCell>
                <TableCell sx={{ color: '#334155', fontSize: '13px' }}>{row.productName}</TableCell>
                <TableCell align="right" sx={{ color: '#475569', fontSize: '13px' }}>
                  {row.files}
                </TableCell>
                <TableCell align="right" sx={{ color: '#475569', fontSize: '13px' }}>
                  {row.sanctioned}
                </TableCell>
                <TableCell align="right" sx={{ color: '#475569', fontSize: '13px' }}>
                  {row.disbursed}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ fontWeight: 600, color: '#0f172a', fontSize: '13px' }}
                >
                  {formatCurrency(row.amount)}
                </TableCell>
                <TableCell align="right">
                  <Chip
                    size="small"
                    label={`${row.conversionPercentage}%`}
                    sx={{
                      fontSize: '11px',
                      fontWeight: 700,
                      borderRadius: '6px',
                      bgcolor: conv >= 75 ? '#dcfce7' : conv >= 50 ? '#e0f2fe' : '#f1f5f9',
                      color: conv >= 75 ? '#15803d' : conv >= 50 ? '#0369a1' : '#64748b'
                    }}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

DetailedBreakdownTable.propTypes = {
  data: PropTypes.array
};

function ProductDataTable({ data }) {
  const tableData = data;
  const total = tableData.reduce((sum, d) => sum + (d.count || 0), 0) || 1;

  return (
    <TableContainer component={Paper} elevation={0} sx={{ border: 'none' }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: '#f8fafc' }}>
            <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '11.5px', py: 1.5 }}>
              PRODUCT
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 700, color: '#475569', fontSize: '11.5px' }}>
              CASES
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 700, color: '#475569', fontSize: '11.5px' }}>
              AMOUNT
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 700, color: '#475569', fontSize: '11.5px' }}>
              SHARE %
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {tableData.map((row, index) => {
            const share = row.share ?? ((row.count / total) * 100).toFixed(1);
            return (
              <TableRow
                key={index}
                sx={{
                  '&:hover': { bgcolor: '#f8fafc' },
                  borderBottom: '1px solid #f1f5f9'
                }}
              >
                <TableCell sx={{ fontWeight: 600, color: '#0f172a', fontSize: '13px', py: 1.5 }}>
                  {row.productName}
                </TableCell>
                <TableCell align="right" sx={{ color: '#334155', fontSize: '13px' }}>
                  {row.count}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ fontWeight: 600, color: '#0f172a', fontSize: '13px' }}
                >
                  {formatCurrency(row.totalAmount)}
                </TableCell>
                <TableCell align="right" sx={{ width: 160 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      justifyContent: 'flex-end'
                    }}
                  >
                    <Box sx={{ width: 60 }}>
                      <LinearProgress
                        variant="determinate"
                        value={Number(share)}
                        sx={{
                          height: 6,
                          borderRadius: 3,
                          bgcolor: '#f1f5f9',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: '#0284c7',
                            borderRadius: 3
                          }
                        }}
                      />
                    </Box>
                    <Typography
                      sx={{ fontSize: '12px', fontWeight: 700, color: '#0284c7', minWidth: 40 }}
                    >
                      {share}%
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

ProductDataTable.propTypes = {
  data: PropTypes.array
};

// ─────────────────────────────────────────────
// MAIN DASHBOARD COMPONENT
// ─────────────────────────────────────────────

const MONTHS = [
  { id: 1, label: 'January' },
  { id: 2, label: 'February' },
  { id: 3, label: 'March' },
  { id: 4, label: 'April' },
  { id: 5, label: 'May' },
  { id: 6, label: 'June' },
  { id: 7, label: 'July' },
  { id: 8, label: 'August' },
  { id: 9, label: 'September' },
  { id: 10, label: 'October' },
  { id: 11, label: 'November' },
  { id: 12, label: 'December' }
];

const DAYS = Array.from({ length: 31 }, (_, i) => ({ id: i + 1, label: String(i + 1) }));
const YEARS = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

export default function DashboardApp() {
  const dispatch = useDispatch();
  const { LoginData } = useLoginSelector();
  const { DashboardData, HierarchyData } = useDashboardSelector();

  const [period, setPeriod] = useState('MTD');
  const [filters, setFilters] = useState({
    day: new Date().getDate(),
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear()
  });

  const [selectedHierarchy, setSelectedHierarchy] = useState({
    partner: null,
    employee: null,
    product: null,
    bank: null
  });
  const [isOpenFilter, setOpenFilter] = useState(false);

  const { LoanTypeDrop, BankDrop } = useGlobalSelector();

  const fetchDashboardData = useCallback(() => {
    if (!LoginData?.userId) return;
    if (period === 'MTD' && !filters.month) return;
    if (period === 'YTD' && !filters.year) return;

    const payload = {
      type: period,
      month: period === 'MTD' ? filters.month : null,
      year: filters.year,
      day: period === 'MTD' ? filters.day : null,
      companyId: LoginData?.companyId,
      loanTypeId: selectedHierarchy?.product?.value || null,
      bankId: selectedHierarchy?.bank?.value || null,
      userId:
        selectedHierarchy.employee?.userId || selectedHierarchy.partner?.userId || LoginData?.userId
    };

    dispatch(getDashboardData(payload));
  }, [dispatch, LoginData, period, filters, selectedHierarchy]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const kpi = DashboardData?.kpiSummary || {
    loginCount: 0,
    loginVolume: 0,
    sanctionedCount: 0,
    sanctionedAmount: 0,
    disbursedCount: 0,
    disbursedAmount: 0,
    totalVolume: 0
  };

  const bankwiseData = DashboardData?.bankwiseData || [];
  const productwiseData = DashboardData?.productwiseData || [];
  const detailedBreakdown = DashboardData?.detailedBreakdown || [];

  function getTopBanks(data, limit = 10) {
    if (!Array.isArray(data)) return [];

    const sorted = [...data].sort((a, b) => b.totalAmount - a.totalAmount);

    const top = sorted.slice(0, limit);
    const rest = sorted.slice(limit);

    if (rest.length === 0) return top;

    const others = rest.reduce(
      (acc, cur) => {
        acc.count += cur.count;
        acc.totalAmount += cur.totalAmount;
        acc.encashmentAmount += cur.encashmentAmount || 0;
        return acc;
      },
      { bankName: 'Others', count: 0, totalAmount: 0, encashmentAmount: 0 }
    );

    return [...top, others];
  }

  const getBankData = useCallback(() => {
    const data = { companyId: LoginData?.companyId, userId: LoginData?.userId };
    dispatch(getBankDropdownData(data));
  }, [dispatch, LoginData]);

  const bankChartData = useMemo(() => getTopBanks(bankwiseData, 100), [bankwiseData]);

  const FetchDashboardHierarchyData = useCallback(() => {
    if (!LoginData?.userId) return;
    const payload = {
      type: period,
      month: period === 'MTD' ? filters.month : null,
      year: filters.year,
      companyId: LoginData?.companyId,
      userId: LoginData?.userId
    };

    dispatch(getHierarchyData(payload));
  }, [dispatch, LoginData, period, filters]);

  useEffect(() => {
    FetchDashboardHierarchyData();
  }, [FetchDashboardHierarchyData]);

  useEffect(() => {
    getBankData();
  }, [getBankData]);

  const partners = HierarchyData?.summary?.data?.partners || [];
  const employees = HierarchyData?.summary?.data?.employees || [];

  useEffect(() => {
    if (period === 'MTD') {
      setSelectedHierarchy({
        partner: null,
        employee: null,
        product: null,
        bank: null
      });
      setFilters({
        day: new Date().getDate(),
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear()
      });
    }

    if (period === 'YTD') {
      setSelectedHierarchy({
        partner: null,
        employee: null,
        product: null,
        bank: null
      });
      setFilters((prev) => ({
        ...prev,
        day: null,
        month: null
      }));
    }
  }, [period]);

  return (
    <Page title="Analytics Dashboard | Real Value Finloan Services">
      <Container maxWidth={false} sx={{ pb: 6, pt: 2, px: { xs: 2, sm: 3, md: 4 } }}>
        <Box sx={{ mb: 4 }}>
          {/* ── Top Header Row ── */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
            sx={{ mb: 2.5, gap: 2 }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                color: '#0f172a',
                fontSize: { xs: '20px', sm: '24px' },
                letterSpacing: '-0.3px',
                textTransform: 'uppercase'
              }}
            >
              ANALYTICS DASHBOARD
            </Typography>

            <Stack direction="row" spacing={1.5} alignItems="center">
              {/* Advanced Filter Toggle */}
              <Tooltip title="Filter options">
                <IconButton
                  onClick={() => setOpenFilter((prev) => !prev)}
                  sx={{
                    borderRadius: '10px',
                    border: '1px solid #e2e8f0',
                    bgcolor: isOpenFilter ? '#e0f2fe' : '#ffffff',
                    color: isOpenFilter ? '#0284c7' : '#64748b',
                    p: 0.9,
                    '&:hover': { bgcolor: '#f8fafc' }
                  }}
                >
                  <FilterListIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>

          {/* ── MTD / YTD Pill Toggle ── */}
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                display: 'inline-flex',
                bgcolor: '#ffffff',
                p: '3px',
                borderRadius: '24px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
              }}
            >
              <Box
                component="button"
                type="button"
                onClick={() => setPeriod('MTD')}
                sx={{
                  border: 'none',
                  outline: 'none',
                  cursor: 'pointer',
                  px: 3.5,
                  py: 0.8,
                  borderRadius: '20px',
                  fontSize: '13px',
                  fontWeight: 700,
                  transition: 'all 0.2s',
                  bgcolor: period === 'MTD' ? '#0284c7' : 'transparent',
                  color: period === 'MTD' ? '#ffffff' : '#64748b',
                  boxShadow: period === 'MTD' ? '0 2px 10px rgba(2,132,199,0.35)' : 'none',
                  '&:hover': {
                    color: period === 'MTD' ? '#ffffff' : '#0284c7'
                  }
                }}
              >
                MTD
              </Box>
              <Box
                component="button"
                type="button"
                onClick={() => setPeriod('YTD')}
                sx={{
                  border: 'none',
                  outline: 'none',
                  cursor: 'pointer',
                  px: 3.5,
                  py: 0.8,
                  borderRadius: '20px',
                  fontSize: '13px',
                  fontWeight: 700,
                  transition: 'all 0.2s',
                  bgcolor: period === 'YTD' ? '#0284c7' : 'transparent',
                  color: period === 'YTD' ? '#ffffff' : '#64748b',
                  boxShadow: period === 'YTD' ? '0 2px 10px rgba(2,132,199,0.35)' : 'none',
                  '&:hover': {
                    color: period === 'YTD' ? '#ffffff' : '#0284c7'
                  }
                }}
              >
                YTD
              </Box>
            </Box>
          </Box>

          {/* ── Advanced Filters Panel (Optional Expansion) ── */}
          {isOpenFilter && (
            <Card
              sx={{
                mb: 3.5,
                p: 2.5,
                borderRadius: '16px',
                border: '1px solid #e2e8f0',
                bgcolor: '#ffffff',
                boxShadow: '0 4px 14px rgba(0,0,0,0.03)'
              }}
            >
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6} md={2.4}>
                  <Autocomplete
                    size="small"
                    clearOnEscape
                    options={LoanTypeDrop || []}
                    getOptionLabel={(option) => option.label || ''}
                    value={selectedHierarchy.product}
                    onChange={(event, newValue) => {
                      setSelectedHierarchy((prev) => ({
                        ...prev,
                        product: newValue
                      }));
                    }}
                    renderInput={(params) => <TextField {...params} label="Loan Type" />}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                  <Autocomplete
                    size="small"
                    options={BankDrop || []}
                    getOptionLabel={(option) => option.label || ''}
                    value={selectedHierarchy.bank}
                    onChange={(event, newValue) => {
                      setSelectedHierarchy((prev) => ({
                        ...prev,
                        bank: newValue
                      }));
                    }}
                    renderInput={(params) => <TextField {...params} label="Bank" />}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                  <Autocomplete
                    size="small"
                    options={partners}
                    getOptionLabel={(option) => option.fullName || ''}
                    value={selectedHierarchy.partner}
                    onChange={(event, newValue) => {
                      setSelectedHierarchy((prev) => ({
                        ...prev,
                        partner: newValue
                      }));
                    }}
                    renderInput={(params) => <TextField {...params} label="Partner" />}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                  <Autocomplete
                    size="small"
                    options={employees}
                    getOptionLabel={(option) => option.fullName || ''}
                    value={selectedHierarchy.employee}
                    onChange={(event, newValue) => {
                      setSelectedHierarchy((prev) => ({
                        ...prev,
                        employee: newValue
                      }));
                    }}
                    renderInput={(params) => <TextField {...params} label="Employee" />}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                  <Autocomplete
                    size="small"
                    clearOnEscape
                    options={DAYS}
                    getOptionLabel={(option) => option.label}
                    value={DAYS.find((d) => d.id === filters.day) ?? null}
                    onChange={(event, newValue) => {
                      setFilters((prev) => ({
                        ...prev,
                        day: newValue?.id ?? null
                      }));
                    }}
                    renderInput={(params) => <TextField {...params} label="Day" />}
                    disabled={period === 'YTD'}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                  <Autocomplete
                    size="small"
                    clearOnEscape
                    options={MONTHS}
                    getOptionLabel={(option) => option.label}
                    value={MONTHS.find((m) => m.id === filters.month)}
                    onChange={(event, newValue) => {
                      setFilters((prev) => ({
                        ...prev,
                        month: newValue ? newValue.id : null
                      }));
                    }}
                    renderInput={(params) => <TextField {...params} label="Month" />}
                    disabled={period === 'YTD'}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                  <Autocomplete
                    size="small"
                    clearOnEscape
                    options={YEARS}
                    value={filters.year}
                    onChange={(event, newValue) => {
                      setFilters((prev) => ({
                        ...prev,
                        year: newValue || null
                      }));
                    }}
                    renderInput={(params) => <TextField {...params} label="Year" />}
                  />
                </Grid>
              </Grid>
            </Card>
          )}

          {/* ── 5 Modern KPI Cards Row ── */}
          <Grid container spacing={2.25} sx={{ mb: 3.5 }}>
            {/* Card 1: Login Cases */}
            <Grid item xs={12} sm={6} md={2.4}>
              <ModernKpiCard
                title="LOGIN CASES"
                icon={<DescriptionOutlinedIcon sx={{ fontSize: 20 }} />}
                value={kpi.loginCount ?? 0}
                subValue={`Volume: ${formatCurrency(kpi.loginVolume || 0)}`}
                cardBg="#f0f9ff"
                borderCol="#e0f2fe"
                iconBg="#e0f2fe"
                iconCol="#0284c7"
              />
            </Grid>

            {/* Card 2: Sanctioned Amount */}
            <Grid item xs={12} sm={6} md={2.4}>
              <ModernKpiCard
                title="SANCTIONED AMOUNT"
                icon={<CheckCircleOutlineIcon sx={{ fontSize: 20 }} />}
                value={kpi.sanctionedCount ?? 0}
                subValue={`Amount: ${formatCurrency(kpi.sanctionedAmount || 0)}`}
                cardBg="#f0fdf4"
                borderCol="#dcfce7"
                iconBg="#dcfce7"
                iconCol="#16a34a"
              />
            </Grid>

            {/* Card 3: Disbursed Amount */}
            <Grid item xs={12} sm={6} md={2.4}>
              <ModernKpiCard
                title="DISBURSED AMOUNT"
                icon={<CurrencyRupeeIcon sx={{ fontSize: 20 }} />}
                value={kpi.disbursedCount ?? 0}
                subValue={`Amount: ${formatCurrency(kpi.disbursedAmount || 0)}`}
                cardBg="#fefce8"
                borderCol="#fef08a"
                iconBg="#fef08a"
                iconCol="#ca8a04"
              />
            </Grid>

            {/* Card 4: Under Sanction Amount */}
            <Grid item xs={12} sm={6} md={2.4}>
              <ModernKpiCard
                title="UNDER SANCTION AMOUNT"
                icon={<CurrencyRupeeIcon sx={{ fontSize: 20 }} />}
                value={kpi.underSanctionCount ?? 0}
                subValue={`Amount: ${formatCurrency(kpi.underSanctionAmount || 0)}`}
                cardBg="#fff1f2"
                borderCol="#fecdd3"
                iconBg="#ffe4e6"
                iconCol="#e11d48"
              />
            </Grid>

            {/* Card 5: Under Disbursal Amount */}
            <Grid item xs={12} sm={6} md={2.4}>
              <ModernKpiCard
                title="UNDER DISBURSAL AMOUNT"
                icon={<CurrencyRupeeIcon sx={{ fontSize: 20 }} />}
                value={kpi.underDisbursementCount ?? 0}
                subValue={`Amount: ${formatCurrency(kpi.underDisbursementAmount || 0)}`}
                cardBg="#faf5ff"
                borderCol="#f3e8ff"
                iconBg="#f3e8ff"
                iconCol="#9333ea"
              />
            </Grid>
          </Grid>

          {/* ── Charts Row (Bank-wise Performance & Product-wise Distribution) ── */}
          <Grid container spacing={3} sx={{ mb: 3.5 }}>
            {/* Bank-wise Performance */}
            <Grid item xs={12} lg={7}>
              <Card
                sx={{
                  borderRadius: '16px',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.03)',
                  p: 2.5,
                  bgcolor: '#ffffff',
                  height: '100%'
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 2
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BarChartIcon sx={{ color: '#0284c7', fontSize: 22 }} />
                    <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#0f172a' }}>
                      Bank-wise Performance
                    </Typography>
                  </Box>
                  <Chip
                    label={period}
                    size="small"
                    sx={{
                      borderRadius: '6px',
                      bgcolor: '#e0f2fe',
                      color: '#0284c7',
                      fontWeight: 700,
                      fontSize: '11px',
                      px: 0.5
                    }}
                  />
                </Box>
                <CardContent sx={{ p: '0 !important' }}>
                  <BankBarChart data={bankChartData} />
                </CardContent>
              </Card>
            </Grid>

            {/* Product-wise Distribution */}
            <Grid item xs={12} lg={5}>
              <Card
                sx={{
                  borderRadius: '16px',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.03)',
                  p: 2.5,
                  bgcolor: '#ffffff',
                  height: '100%'
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 2
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <GridViewIcon sx={{ color: '#0284c7', fontSize: 20 }} />
                    <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#0f172a' }}>
                      Product-wise Distribution
                    </Typography>
                  </Box>
                  <Chip
                    label={period}
                    size="small"
                    sx={{
                      borderRadius: '6px',
                      bgcolor: '#e0f2fe',
                      color: '#0284c7',
                      fontWeight: 700,
                      fontSize: '11px',
                      px: 0.5
                    }}
                  />
                </Box>
                <CardContent sx={{ p: '0 !important' }}>
                  <ProductDonutChart data={productwiseData} />
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* ── Bottom Detailed Tables (Bank-wise Breakdown & Product-wise Breakdown) ── */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {/* Table 1: Bank-wise Detailed Breakdown */}
            <Grid item xs={12}>
              <Card
                sx={{
                  borderRadius: '16px',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.03)',
                  p: { xs: 2, sm: 2.5 },
                  bgcolor: '#ffffff'
                }}
              >
                <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#0f172a', mb: 2 }}>
                  Bank-wise Detailed Breakdown
                </Typography>
                <DetailedBreakdownTable data={detailedBreakdown} />
              </Card>
            </Grid>

            {/* Table 2: Product-wise Breakdown */}
            <Grid item xs={12}>
              <Card
                sx={{
                  borderRadius: '16px',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.03)',
                  p: { xs: 2, sm: 2.5 },
                  bgcolor: '#ffffff'
                }}
              >
                <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#0f172a', mb: 2 }}>
                  Product-wise Breakdown
                </Typography>
                <ProductDataTable data={productwiseData} />
              </Card>
            </Grid>
          </Grid>

          {/* ── Footer ── */}
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography sx={{ fontSize: '12px', color: '#64748b' }}>
              Copyright © 2026{' '}
              <a
                href="https://kalolytic.com"
                target="_blank"
                rel="noreferrer"
                style={{ color: '#0284c7', textDecoration: 'underline', fontWeight: 600 }}
              >
                Kalolytic Solutions
              </a>
              . All rights reserved.
            </Typography>
          </Box>
        </Box>
      </Container>
    </Page>
  );
}
